/**
 * wayai conversations — list conversations or show conversation detail
 *
 * Usage:
 *   wayai conversations                                       # list conversations
 *   wayai conversations <conversation_id>                     # show detail + messages
 *   wayai conversations <conversation_id> observability       # list per-message LLM traces
 *   wayai conversations <conversation_id> observability --message-id <id>
 *   wayai conversations --status ended                        # filter by status
 *   wayai conversations --period 7d                           # analytics-powered list
 *   wayai conversations --from 2026-01-01 --to 2026-01-31
 *   wayai conversations --json                                # raw JSON output
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { parsePeriod, parseDateArg, wantsHelp } from '../lib/utils.js';
import { runConversationObservability } from './observability.js';

/**
 * Command-specific help. `conversations` is in `OWN_HELP_COMMANDS` (lib/utils.ts) so
 * `wayai conversations --help` falls through to here instead of printing the
 * global command list — without this, the `observability` subcommand is
 * undiscoverable via `--help` and agents guess at invented syntax.
 */
function printConversationsHelp(): void {
  console.error(`
wayai conversations — list conversations or inspect what an agent received

Usage:
  wayai conversations                                  List conversations
  wayai conversations --status <agent|team|ended>      Filter by status
  wayai conversations --period 7d                      Analytics-powered list
  wayai conversations --from <date> --to <date>        List within a date range
  wayai conversations <conversation_id>                Show detail + messages
  wayai conversations <conversation_id> observability   List the LLM turns (per-message id, latency, tool_calls)
  wayai conversations <conversation_id> observability --message-id <id>
  wayai conversations <conversation_id> annotate --set key=value [--type numeric|categorical|text]
                                                       Set a post-hoc business outcome (e.g. customer_purchased=true)
                                                       on an ended conversation, as an analytics dimension. Repeat
                                                       --set for multiple keys. Needs the hub kept within its
                                                       conversation retention window.
                                                       Full record for one turn:
                                                       resolved system prompt, the exact
                                                       messages the model saw (rendered
                                                       additional_context, injected
                                                       timestamps, replayed history),
                                                       completion, tool calls, tokens

Options:
  --message-id <id>   Expand one turn (only with the \`observability\` subcommand)
  --limit <n>         Max rows (list view)
  --offset <n>        Skip rows (list view)
  --json              Raw JSON output

The default text list omits message ids — use \`--json\` or the \`observability\`
subcommand to discover them. \`observability\` is the answer to "the agent did X —
what did it ACTUALLY see?".
`.trim());
}

export async function conversationsCommand(args: string[]): Promise<void> {
  if (wantsHelp(args)) {
    printConversationsHelp();
    return;
  }

  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  // Parse args
  let status: 'agent' | 'team' | 'ended' | undefined;
  let conversationId: string | undefined;
  let subcommand: string | undefined;
  let messageId: string | undefined;
  let limit: number | undefined;
  let offset: number | undefined;
  let period: string | undefined;
  let from: string | undefined;
  let to: string | undefined;
  let jsonOutput = false;
  const annotations: Array<{ key: string; value: string }> = [];
  let annotationType: 'numeric' | 'categorical' | 'text' | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--status') {
      const val = args[++i];
      if (val === 'agent' || val === 'team' || val === 'ended') {
        status = val;
      } else {
        console.error(`Invalid status: ${val}. Must be 'agent', 'team', or 'ended'.`);
        process.exit(1);
      }
    } else if (arg === '--limit') {
      limit = parseInt(args[++i], 10);
    } else if (arg === '--offset') {
      offset = parseInt(args[++i], 10);
    } else if (arg === '--message-id') {
      if (i + 1 >= args.length) { console.error('--message-id requires a value'); process.exit(1); }
      messageId = args[++i];
    } else if (arg === '--period') {
      if (i + 1 >= args.length) { console.error('--period requires a value'); process.exit(1); }
      period = args[++i];
    } else if (arg === '--from') {
      if (i + 1 >= args.length) { console.error('--from requires a value'); process.exit(1); }
      from = args[++i];
    } else if (arg === '--to') {
      if (i + 1 >= args.length) { console.error('--to requires a value'); process.exit(1); }
      to = args[++i];
    } else if (arg === '--set') {
      if (i + 1 >= args.length) { console.error('--set requires key=value'); process.exit(1); }
      const pair = args[++i];
      const eq = pair.indexOf('=');
      if (eq <= 0) { console.error(`--set must be key=value (got: ${pair})`); process.exit(1); }
      annotations.push({ key: pair.slice(0, eq), value: pair.slice(eq + 1) });
    } else if (arg === '--type') {
      const val = args[++i];
      if (val === 'numeric' || val === 'categorical' || val === 'text') {
        annotationType = val;
      } else {
        console.error(`Invalid --type: ${val}. Must be 'numeric', 'categorical', or 'text'.`);
        process.exit(1);
      }
    } else if (arg === '--json') {
      jsonOutput = true;
    } else if (!arg.startsWith('-')) {
      // Position 1: conversation_id. Position 2: subcommand (e.g. `observability`).
      // If the user puts the subcommand first ("wayai conversations observability"),
      // promote it to subcommand directly — that yields a clearer "missing
      // conversation_id" error instead of pretending `observability` is a UUID.
      if (conversationId === undefined && arg === 'observability') {
        subcommand = arg;
      } else if (conversationId === undefined) {
        conversationId = arg;
      } else if (subcommand === undefined) {
        subcommand = arg;
      } else {
        console.error(`Unexpected positional argument: ${arg}`);
        process.exit(1);
      }
    }
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  const hasAnalyticsFlags = !!(period || from || to);

  if (subcommand === 'observability') {
    if (!conversationId) {
      console.error('Usage: wayai conversations <conversation_id> observability [--message-id X] [--json]');
      process.exit(1);
    }
    try {
      await runConversationObservability(client, hubId, conversationId, { messageId, jsonOutput });
    } catch (err) {
      // Raw ApiError text isn't user-friendly at the CLI surface.
      if (err instanceof ApiError && err.status === 404) {
        console.error('Observability data not found. Either the conversation has no LLM turns yet, or R2 entries were purged.');
        process.exit(1);
      }
      // A retryable cold-start (CLICKHOUSE_COLD) that outlived the ApiClient's
      // bounded retry is transient, not a crash — surface it as such (and it's
      // already kept out of Sentry via ApiError.isRetryable; gh #2883).
      if (err instanceof ApiError && err.isRetryable) {
        console.error('ClickHouse is warming up (cold start); observability is temporarily unavailable. Retry in a few seconds.');
        process.exit(1);
      }
      throw err;
    }
    return;
  }

  if (subcommand === 'annotate') {
    if (!conversationId) {
      console.error('Usage: wayai conversations <conversation_id> annotate --set key=value [--type numeric|categorical|text]');
      process.exit(1);
    }
    if (annotations.length === 0) {
      console.error('annotate requires at least one --set key=value');
      process.exit(1);
    }
    const results: Array<{ key: string; type: 'numeric' | 'categorical' | 'text' }> = [];
    for (const { key, value } of annotations) {
      // Infer type when --type is omitted: numeric if the value parses as a finite
      // number, else categorical. The backend re-validates and coerces per type.
      const inferred: 'numeric' | 'categorical' =
        value.trim() !== '' && Number.isFinite(Number(value)) ? 'numeric' : 'categorical';
      const type = annotationType ?? inferred;
      await client.annotateConversation(hubId, conversationId, { key, value, type });
      results.push({ key, type });
    }
    if (jsonOutput) {
      console.log(JSON.stringify({ conversation_id: conversationId, annotations: results }, null, 2));
    } else {
      for (const r of results) console.log(`✓ Set annotation ${r.key} (${r.type})`);
    }
    return;
  }

  if (subcommand) {
    console.error(`Unknown conversations subcommand: ${subcommand}`);
    process.exit(1);
  }

  // `--message-id` is only meaningful with the `observability` subcommand.
  // Reject it elsewhere so a user typo (e.g. `wayai conversations <id> --message-id X`,
  // forgetting the subcommand) doesn't silently drop the flag.
  if (messageId !== undefined) {
    console.error('--message-id is only valid with the `observability` subcommand. Did you mean: wayai conversations <id> observability --message-id ' + messageId + '?');
    process.exit(1);
  }

  if (conversationId) {
    const [result, schedules] = await Promise.all([
      client.getConversation(hubId, conversationId, { limit, offset }),
      client.getPendingSchedules(hubId, conversationId).catch(() => ({ entries: [], total: 0 })),
    ]);

    if (jsonOutput) {
      console.log(JSON.stringify({ ...result, pending_schedules: schedules }, null, 2));
      return;
    }

    console.log(`Conversation: ${result.conversation.conversation_id}`);
    console.log(`Status: ${result.conversation.conversation_status}`);
    if (result.conversation.kanban_status) {
      console.log(`Kanban: ${result.conversation.kanban_status}`);
    }
    const user = result.conversation.user_name || result.conversation.user_email;
    if (user) {
      console.log(`User: ${user}`);
    }
    console.log(`Messages: ${result.total_messages}`);
    console.log('');

    for (const msg of result.messages) {
      const time = new Date(msg.created_at).toLocaleString();
      const meta = (msg.metadata ?? {}) as { tool_name?: unknown; arguments?: unknown; result?: unknown; is_error?: unknown };
      const toolName = typeof meta.tool_name === 'string' ? meta.tool_name : undefined;
      const isToolCall = msg.sender_type === 'agent' && msg.receiver_type === 'tool' && toolName;
      const isToolResult = msg.sender_type === 'tool' && msg.receiver_type === 'agent' && toolName;

      if (isToolCall) {
        console.log(`[tool_call: ${toolName}] (${time})`);
        if (meta.arguments !== undefined) {
          console.log(JSON.stringify(meta.arguments, null, 2));
        }
      } else if (isToolResult) {
        const label = meta.is_error === true ? 'tool_error' : 'tool_result';
        console.log(`[${label}: ${toolName}] (${time})`);
        if (meta.result !== undefined) {
          console.log(typeof meta.result === 'string' ? meta.result : JSON.stringify(meta.result, null, 2));
        } else if (msg.message_text) {
          console.log(msg.message_text);
        }
      } else if (msg.sender_type === 'system') {
        console.log(`[system → ${msg.receiver_type ?? '-'}] (${time})`);
        console.log(msg.message_text || '(no text)');
      } else {
        const from = msg.sender_description || msg.sender_type;
        const to = msg.receiver_description || msg.receiver_type;
        const routing = to ? `${from} → ${to}` : from;
        console.log(`[${routing}] (${time})`);
        console.log(msg.message_text || '(no text)');
      }
      console.log('');
    }

    // Show pending scheduled actions
    if (schedules.entries.length > 0) {
      console.log(`Pending Scheduled Actions (${schedules.entries.length}):`);
      for (const entry of schedules.entries) {
        const type = entry.schedule_type;
        const time = new Date(entry.run_at).toLocaleString();
        const order = entry.followup_order ? `#${entry.followup_order}` : '';
        const preview = entry.message_text.length > 50
          ? entry.message_text.slice(0, 47) + '...'
          : entry.message_text;
        console.log(`  ${type} ${order} — ${time}`);
        console.log(`    "${preview}"`);
      }
      console.log('');
    }
  } else if (hasAnalyticsFlags) {
    // Analytics-powered list
    if (status) {
      console.error('The --status flag is not supported with --period/--from/--to. Without these flags, --status works as before.');
      process.exit(1);
    }

    if ((from && !to) || (!from && to)) {
      console.error('--from and --to must both be provided together.');
      process.exit(1);
    }

    let start: string;
    let end: string;

    try {
      if (from && to) {
        start = parseDateArg(from, 'from');
        end = parseDateArg(to, 'to');
        if (new Date(start).getTime() >= new Date(end).getTime()) {
          console.error('--from must be before --to.');
          process.exit(1);
        }
      } else {
        const parsed = parsePeriod(period || '7d');
        start = parsed.start;
        end = parsed.end;
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }

    const result = await client.getAnalyticsConversations({
      hub_id: hubId,
      time_interval: { start, end },
      limit: limit ?? 50,
      offset: offset ?? 0,
    });

    if (jsonOutput) {
      // Emit the full payload (the `cold` flag is part of it) so machine
      // consumers can inspect it, then exit non-zero on cold so batch/automation
      // pipelines treat a degraded listing as retryable rather than as a
      // complete "no conversations" result (gh #2452).
      console.log(JSON.stringify(result, null, 2));
      if (result.cold) process.exit(1);
      return;
    }

    // A cold result is a transient ClickHouse cold-start, NOT an empty hub.
    // Surface it distinctly — silently printing "No conversations found." here
    // is what made dozens of real ended conversations look undiscoverable in
    // gh #2452.
    if (result.cold) {
      console.error(
        'ClickHouse is warming up (cold start); the ended-conversation list is temporarily unavailable. Retry in a few seconds.',
      );
      process.exit(1);
    }

    if (result.conversations.length === 0) {
      console.log('No conversations found.');
      return;
    }

    console.log(`Conversations (${result.conversations.length} of ${result.total}):\n`);

    for (const conv of result.conversations) {
      const participant = conv.participant_name || conv.participant_phone || 'unknown';
      const updated = conv.updated_at ? new Date(conv.updated_at).toLocaleString() : '-';
      const msgs = conv.message_count != null ? `${conv.message_count} msgs` : '';
      console.log(`  ${conv.conversation_id}  ${participant.padEnd(20)}  ${msgs.padEnd(10)}  ${updated}`);
    }
  } else {
    // Original list path (backward compat)
    const result = await client.getConversations(hubId, { status, limit, offset });

    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
      return;
    }

    if (result.conversations.length === 0) {
      console.log('No conversations found.');
      return;
    }

    console.log(`Conversations (${result.conversations.length} of ${result.total}):\n`);

    for (const conv of result.conversations) {
      const user = conv.user_name || conv.user_email || 'unknown';
      const updated = conv.updated_at ? new Date(conv.updated_at).toLocaleString() : '-';
      console.log(`  ${conv.conversation_id}  ${conv.conversation_status.padEnd(7)}  ${user.padEnd(20)}  ${updated}`);
    }
  }
}
